import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Wrench,
  Calendar,
  UserCheck,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  History,
  RefreshCw,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Mail,
  Send,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MachineMaintenance = () => {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [alerts, setAlerts] = useState({ alerts: [], reminders: [] });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("schedules");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [emailLog, setEmailLog] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState(null);

  // Form states
  const [formMachineId, setFormMachineId] = useState("");
  const [formInterval, setFormInterval] = useState("30");
  const [formNextDate, setFormNextDate] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [completedBy, setCompletedBy] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [completeDate, setCompleteDate] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [schedRes, histRes, machRes, empRes, alertRes, logRes] = await Promise.all([
        axios.get(`${API}/maintenance/schedules`),
        axios.get(`${API}/maintenance/history`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/maintenance/employees`),
        axios.get(`${API}/maintenance/alerts`),
        axios.get(`${API}/maintenance/email-log`),
      ]);
      setSchedules(schedRes.data);
      setHistory(histRes.data);
      setMachines(machRes.data);
      setEmployees(empRes.data);
      setAlerts(alertRes.data);
      setEmailLog(logRes.data.logs || []);
    } catch (err) {
      toast.error("Failed to load maintenance data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCreate = async () => {
    if (!formMachineId || !formNextDate) {
      toast.error("Select a machine and set the next service date");
      return;
    }
    try {
      await axios.post(`${API}/maintenance/schedules`, {
        machine_id: formMachineId,
        interval_days: parseInt(formInterval) || 30,
        next_service_date: formNextDate,
        notes: formNotes,
      });
      toast.success("Maintenance schedule created");
      setShowCreateDialog(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create schedule");
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormMachineId(schedule.machine_id);
    setFormInterval(String(schedule.interval_days || 30));
    setFormNextDate(schedule.next_service_date || "");
    setFormNotes(schedule.notes || "");
    setShowEditDialog(true);
  };

  const handleEdit = async () => {
    if (!formNextDate) {
      toast.error("Next service date is required");
      return;
    }
    try {
      await axios.put(`${API}/maintenance/schedules/${selectedSchedule.schedule_id}`, {
        interval_days: parseInt(formInterval) || 30,
        next_service_date: formNextDate,
        notes: formNotes,
      });
      toast.success("Schedule updated");
      setShowEditDialog(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update schedule");
    }
  };

  const handleAssign = async () => {
    if (!assignTo || !selectedSchedule) return;
    try {
      await axios.put(`${API}/maintenance/schedules/${selectedSchedule.schedule_id}/assign`, {
        assigned_to: assignTo,
        assigned_by: "management",
      });
      toast.success(`Assigned to ${assignTo}`);
      setShowAssignDialog(false);
      setAssignTo("");
      fetchAll();
    } catch (err) {
      toast.error("Failed to assign");
    }
  };

  const handleComplete = async () => {
    if (!completedBy || !selectedSchedule) return;
    try {
      const res = await axios.put(`${API}/maintenance/schedules/${selectedSchedule.schedule_id}/complete`, {
        completed_by: completedBy,
        notes: completeNotes,
        completed_date: completeDate || new Date().toISOString(),
      });
      toast.success(`Maintenance completed. Next service: ${res.data.next_service_date}`);
      setShowCompleteDialog(false);
      setCompletedBy("");
      setCompleteNotes("");
      setCompleteDate("");
      fetchAll();
    } catch (err) {
      toast.error("Failed to complete maintenance");
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm("Delete this maintenance schedule?")) return;
    try {
      await axios.delete(`${API}/maintenance/schedules/${scheduleId}`);
      toast.success("Schedule deleted");
      fetchAll();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormMachineId("");
    setFormInterval("30");
    setFormNextDate("");
    setFormNotes("");
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/maintenance/send-digest`);
      if (res.data.email_sent) {
        toast.success(`Digest sent: ${res.data.alerts || 0} alerts, ${res.data.reminders || 0} upcoming`);
      } else {
        toast.info(res.data.message || "No maintenance alerts to send");
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendReminder = async (scheduleId, machineName) => {
    setSendingReminderId(scheduleId);
    try {
      await axios.post(`${API}/maintenance/schedules/${scheduleId}/send-reminder`);
      toast.success(`Reminder email sent for ${machineName}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminderId(null);
    }
  };

  const getStatusBadge = (schedule) => {
    const today = new Date().toISOString().split("T")[0];
    const svcDate = schedule.next_service_date;
    if (!svcDate) return <span className="px-2 py-1 rounded text-xs bg-gray-700 text-gray-300">No date</span>;

    if (svcDate < today) {
      const days = Math.floor((new Date(today) - new Date(svcDate)) / 86400000);
      return <span className="px-2 py-1 rounded text-xs bg-red-900/50 text-red-400 font-semibold">{days}d Overdue</span>;
    }
    if (svcDate === today) {
      return <span className="px-2 py-1 rounded text-xs bg-amber-900/50 text-amber-400 font-semibold animate-pulse">Due Today</span>;
    }
    const daysUntil = Math.floor((new Date(svcDate) - new Date(today)) / 86400000);
    if (daysUntil <= 3) {
      return <span className="px-2 py-1 rounded text-xs bg-yellow-900/50 text-yellow-400">{daysUntil}d away</span>;
    }
    return <span className="px-2 py-1 rounded text-xs bg-emerald-900/50 text-emerald-400">{daysUntil}d away</span>;
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Machines that don't have a schedule yet
  const unscheduledMachines = machines.filter(m => !schedules.some(s => s.machine_id === m.id));

  const totalAlerts = (alerts.alerts?.length || 0) + (alerts.reminders?.length || 0);

  return (
    <div className="space-y-6" data-testid="machine-maintenance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-cyan-400 flex items-center gap-2" data-testid="maintenance-title">
            <Wrench className="w-6 h-6" />
            Machine Maintenance
          </h2>
          <p className="text-gray-400 text-sm mt-1">Schedule, assign, and track machine service & maintenance</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendDigest}
            disabled={sendingDigest}
            className="border-purple-600 text-purple-300 hover:bg-purple-900/30"
            data-testid="send-digest-btn"
          >
            <Mail className="w-4 h-4 mr-1" /> {sendingDigest ? "Sending..." : "Send Daily Report"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            className="border-gray-600 text-gray-300"
            data-testid="maintenance-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="maintenance-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Schedule
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-cyan-400">{schedules.length}</div>
            <div className="text-xs text-gray-400 mt-1">Active Schedules</div>
          </CardContent>
        </Card>
        <Card className={`border-gray-700 ${alerts.alerts?.length > 0 ? 'bg-red-950/40 border-red-800' : 'bg-gray-900/50'}`}>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${alerts.alerts?.length > 0 ? 'text-red-400 animate-pulse' : 'text-gray-400'}`}>
              {alerts.alerts?.length || 0}
            </div>
            <div className="text-xs text-gray-400 mt-1">Due / Overdue</div>
          </CardContent>
        </Card>
        <Card className={`border-gray-700 ${alerts.reminders?.length > 0 ? 'bg-amber-950/30 border-amber-800' : 'bg-gray-900/50'}`}>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${alerts.reminders?.length > 0 ? 'text-amber-400' : 'text-gray-400'}`}>
              {alerts.reminders?.length || 0}
            </div>
            <div className="text-xs text-gray-400 mt-1">Upcoming (3 days)</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{history.length}</div>
            <div className="text-xs text-gray-400 mt-1">Completed Services</div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Banner */}
      {totalAlerts > 0 && (
        <Card className="bg-gradient-to-r from-red-950/40 via-amber-950/30 to-red-950/40 border-red-800/50" data-testid="maintenance-alert-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-semibold">Maintenance Alerts</span>
            </div>
            <div className="space-y-2">
              {alerts.alerts?.map(a => (
                <div key={a.schedule_id} className="flex items-center justify-between bg-red-900/20 rounded-lg px-3 py-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{a.machine_name}</span>
                      <span className="text-red-400 text-sm">
                        {a.status === "overdue" ? `${a.days_overdue}d overdue` : "Due Today"}
                      </span>
                      {a.assigned_to && <span className="text-gray-400 text-sm">Assigned: {a.assigned_to}</span>}
                    </div>
                    {a.notes && (
                      <p className="text-amber-300 text-sm mt-1 bg-amber-900/20 px-2 py-1 rounded inline-block">{a.notes}</p>
                    )}
                  </div>
                  {!a.assigned_to && (
                    <Button size="sm" variant="outline" className="border-red-700 text-red-400 text-xs ml-2"
                      onClick={() => { setSelectedSchedule(a); setShowAssignDialog(true); }}>
                      <UserCheck className="w-3 h-3 mr-1" /> Assign
                    </Button>
                  )}
                </div>
              ))}
              {alerts.reminders?.map(r => (
                <div key={r.schedule_id} className="flex items-center justify-between bg-amber-900/20 rounded-lg px-3 py-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{r.machine_name}</span>
                      <span className="text-amber-400 text-sm">in {r.days_until}d ({r.next_service_date})</span>
                      {r.assigned_to && <span className="text-gray-400 text-sm">Assigned: {r.assigned_to}</span>}
                    </div>
                    {r.notes && (
                      <p className="text-amber-300 text-sm mt-1 bg-amber-900/20 px-2 py-1 rounded inline-block">{r.notes}</p>
                    )}
                  </div>
                  {!r.assigned_to && (
                    <Button size="sm" variant="outline" className="border-amber-700 text-amber-400 text-xs ml-2"
                      onClick={() => { setSelectedSchedule(r); setShowAssignDialog(true); }}>
                      <UserCheck className="w-3 h-3 mr-1" /> Assign
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeView === "schedules" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveView("schedules")}
          className={activeView === "schedules" ? "bg-cyan-700" : "border-gray-600 text-gray-400"}
          data-testid="view-schedules-btn"
        >
          <Calendar className="w-4 h-4 mr-1" /> Schedules ({schedules.length})
        </Button>
        <Button
          variant={activeView === "history" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveView("history")}
          className={activeView === "history" ? "bg-cyan-700" : "border-gray-600 text-gray-400"}
          data-testid="view-history-btn"
        >
          <History className="w-4 h-4 mr-1" /> History ({history.length})
        </Button>
        <Button
          variant={activeView === "emaillog" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveView("emaillog")}
          className={activeView === "emaillog" ? "bg-cyan-700" : "border-gray-600 text-gray-400"}
          data-testid="view-emaillog-btn"
        >
          <Mail className="w-4 h-4 mr-1" /> Email Log ({emailLog.length})
        </Button>
      </div>

      {/* Schedules Table */}
      {activeView === "schedules" && (
        <Card className="bg-gray-900/30 border-gray-700" data-testid="schedules-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-cyan-400">Active Maintenance Schedules</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No maintenance schedules yet. Click "Add Schedule" to create one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-400">Machine</TableHead>
                      <TableHead className="text-gray-400">Interval</TableHead>
                      <TableHead className="text-gray-400">Next Service</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Notes</TableHead>
                      <TableHead className="text-gray-400">Assigned To</TableHead>
                      <TableHead className="text-gray-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map((s) => (
                      <Fragment key={s.schedule_id}>
                        <TableRow className="border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                          onClick={() => toggleRow(s.schedule_id)} data-testid={`schedule-row-${s.schedule_id}`}>
                          <TableCell className="text-white font-medium">
                            <div className="flex items-center gap-2">
                              {expandedRows[s.schedule_id] ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                              <Wrench className="w-4 h-4 text-cyan-500" />
                              {s.machine_name || s.machine_id}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300">{s.interval_days} days</TableCell>
                          <TableCell className="text-gray-300">{s.next_service_date || "—"}</TableCell>
                          <TableCell>{getStatusBadge(s)}</TableCell>
                          <TableCell>
                            {s.notes ? (
                              <span className="text-amber-300 bg-amber-900/30 px-2 py-1 rounded text-xs font-medium max-w-[180px] truncate inline-block" title={s.notes}>
                                {s.notes}
                              </span>
                            ) : (
                              <span className="text-gray-600 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.assigned_to ? (
                              <span className="text-emerald-400 flex items-center gap-1">
                                <UserCheck className="w-3 h-3" /> {s.assigned_to}
                              </span>
                            ) : (
                              <span className="text-gray-500 text-sm">Unassigned</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" className="text-purple-400 hover:text-purple-300 h-8 px-2"
                                data-testid={`remind-btn-${s.schedule_id}`}
                                title="Send email reminder"
                                disabled={sendingReminderId === s.schedule_id}
                                onClick={() => handleSendReminder(s.schedule_id, s.machine_name || s.machine_id)}>
                                {sendingReminderId === s.schedule_id ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2"
                                data-testid={`edit-btn-${s.schedule_id}`}
                                title="Edit schedule"
                                onClick={() => openEditDialog(s)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 h-8 px-2"
                                data-testid={`assign-btn-${s.schedule_id}`}
                                title="Assign person"
                                onClick={() => { setSelectedSchedule(s); setShowAssignDialog(true); }}>
                                <UserCheck className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-8 px-2"
                                data-testid={`complete-btn-${s.schedule_id}`}
                                onClick={() => { setSelectedSchedule(s); setCompleteDate(new Date().toISOString().split("T")[0]); setShowCompleteDialog(true); }}>
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2"
                                data-testid={`delete-btn-${s.schedule_id}`}
                                onClick={() => handleDelete(s.schedule_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedRows[s.schedule_id] && (
                          <TableRow className="border-gray-800 bg-gray-900/50">
                            <TableCell colSpan={7} className="p-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div><span className="text-gray-500">Machine ID:</span> <span className="text-gray-300 ml-1">{s.machine_id}</span></div>
                                <div><span className="text-gray-500">Created:</span> <span className="text-gray-300 ml-1">{s.created_at?.split("T")[0]}</span></div>
                                <div><span className="text-gray-500">Assigned By:</span> <span className="text-gray-300 ml-1">{s.assigned_by || "—"}</span></div>
                                <div><span className="text-gray-500">Notes:</span> <span className="text-amber-300 ml-1">{s.notes || "—"}</span></div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      {activeView === "history" && (
        <Card className="bg-gray-900/30 border-gray-700" data-testid="history-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-cyan-400">Maintenance History</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No maintenance history yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-400">Machine</TableHead>
                      <TableHead className="text-gray-400">Completed By</TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                      <TableHead className="text-gray-400">Assigned To</TableHead>
                      <TableHead className="text-gray-400">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.history_id} className="border-gray-800">
                        <TableCell className="text-white font-medium">{h.machine_name || h.machine_id}</TableCell>
                        <TableCell className="text-emerald-400">{h.completed_by}</TableCell>
                        <TableCell className="text-gray-300">{h.completed_date?.split("T")[0]}</TableCell>
                        <TableCell className="text-gray-400">{h.assigned_to || "—"}</TableCell>
                        <TableCell className="text-gray-400 max-w-[200px] truncate">{h.notes || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Log Table */}
      {activeView === "emaillog" && (
        <Card className="bg-gray-900/30 border-gray-700" data-testid="emaillog-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email Notification Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailLog.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No emails sent yet. Use "Send Daily Report" or per-machine reminders.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-700">
                      <TableHead className="text-gray-400">Date</TableHead>
                      <TableHead className="text-gray-400">Type</TableHead>
                      <TableHead className="text-gray-400">Details</TableHead>
                      <TableHead className="text-gray-400">Trigger</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLog.map((log) => (
                      <TableRow key={log.id} className="border-gray-800">
                        <TableCell className="text-gray-300 whitespace-nowrap">
                          {log.created_at?.split("T")[0]}{" "}
                          <span className="text-gray-500 text-xs">{log.created_at?.split("T")[1]?.slice(0, 5)}</span>
                        </TableCell>
                        <TableCell>
                          {log.type === "daily_digest" ? (
                            <span className="px-2 py-1 rounded text-xs bg-purple-900/50 text-purple-300 font-medium">Daily Digest</span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs bg-cyan-900/50 text-cyan-300 font-medium">Single Reminder</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-300 text-sm">
                          {log.type === "daily_digest" ? (
                            <span>
                              {log.overdue_count > 0 && <span className="text-red-400 mr-2">{log.overdue_count} overdue</span>}
                              {log.due_today_count > 0 && <span className="text-amber-400 mr-2">{log.due_today_count} due today</span>}
                              {log.upcoming_count > 0 && <span className="text-emerald-400">{log.upcoming_count} upcoming</span>}
                            </span>
                          ) : (
                            <span className="text-white">{log.machine_name || "—"}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded ${log.trigger === "automatic" ? "bg-blue-900/40 text-blue-300" : "bg-gray-700 text-gray-300"}`}>
                            {log.trigger === "automatic" ? "Auto" : "Manual"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {log.email_sent ? (
                            <span className="text-emerald-400 flex items-center gap-1 text-sm">
                              <CheckCircle className="w-3 h-3" /> Sent
                            </span>
                          ) : (
                            <span className="text-red-400 flex items-center gap-1 text-sm">
                              <AlertTriangle className="w-3 h-3" /> Failed
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Schedule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="create-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <Plus className="w-5 h-5" /> New Maintenance Schedule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Machine</Label>
              <Select value={formMachineId} onValueChange={setFormMachineId}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-machine">
                  <SelectValue placeholder="Select machine..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {machines.map(m => (
                    <SelectItem key={m.id} value={m.id} className="text-white">{m.id} - {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {unscheduledMachines.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{unscheduledMachines.length} machines without a schedule</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Interval (days)</Label>
                <Input type="number" value={formInterval} onChange={e => setFormInterval(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white" data-testid="input-interval" />
              </div>
              <div>
                <Label className="text-gray-300">Next Service Date</Label>
                <Input type="date" value={formNextDate} onChange={e => setFormNextDate(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white" data-testid="input-next-date" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Maintenance Notes</Label>
              <textarea value={formNotes} onChange={e => setFormNotes(e.target.value)}
                placeholder="e.g., Oil change, belt replacement, bearing check..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                data-testid="input-notes" />
              <p className="text-xs text-gray-500 mt-1">This note will appear in maintenance alerts and notifications</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-schedule-btn">
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <UserCheck className="w-5 h-5" /> Assign Maintenance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Assign someone for <span className="text-white font-medium">{selectedSchedule?.machine_name}</span> maintenance
              {selectedSchedule?.next_service_date && (
                <span className="text-amber-400"> on {selectedSchedule.next_service_date}</span>
              )}
            </p>
            <div>
              <Label className="text-gray-300">Assign To</Label>
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-assign-to">
                  <SelectValue placeholder="Select person..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {employees.map(e => (
                    <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleAssign} className="bg-cyan-600 hover:bg-cyan-700" data-testid="confirm-assign-btn">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Complete Maintenance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Mark <span className="text-white font-medium">{selectedSchedule?.machine_name}</span> maintenance as complete
            </p>
            <div>
              <Label className="text-gray-300">Completed By</Label>
              <Select value={completedBy} onValueChange={setCompletedBy}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-completed-by">
                  <SelectValue placeholder="Who completed it..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {employees.map(e => (
                    <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Completion Date</Label>
              <Input type="date" value={completeDate} onChange={e => setCompleteDate(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white" data-testid="input-complete-date" />
            </div>
            <div>
              <Label className="text-gray-300">Service Notes</Label>
              <textarea value={completeNotes} onChange={e => setCompleteNotes(e.target.value)}
                placeholder="Service details, parts replaced, observations..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                data-testid="input-complete-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-complete-btn">
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="edit-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <Edit className="w-5 h-5" /> Edit Maintenance Schedule
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-800/50 rounded-lg px-3 py-2">
              <span className="text-gray-400 text-sm">Machine:</span>
              <span className="text-white font-medium ml-2">{selectedSchedule?.machine_name || selectedSchedule?.machine_id}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Interval (days)</Label>
                <Input type="number" value={formInterval} onChange={e => setFormInterval(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white" data-testid="edit-interval" />
              </div>
              <div>
                <Label className="text-gray-300">Next Service Date</Label>
                <Input type="date" value={formNextDate} onChange={e => setFormNextDate(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white" data-testid="edit-next-date" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Maintenance Notes</Label>
              <textarea value={formNotes} onChange={e => setFormNotes(e.target.value)}
                placeholder="e.g., Oil change, belt replacement, bearing check..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                data-testid="edit-notes" />
              <p className="text-xs text-gray-500 mt-1">This note will appear in maintenance alerts and notifications</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleEdit} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MachineMaintenance;
